import React, { useState } from 'react';
import { ChartSetList, SavedChartState } from '../types';
import { SetlistList, SetlistDetail, ChartPicker, CreateSetlistForm } from './collections';

interface CollectionManagerProps {
  collections: ChartSetList[];
  savedChartsData: SavedChartState[];
  onCreateCollection: (collection: Omit<ChartSetList, 'id' | 'createdAt' | 'lastModified'>) => void;
  onUpdateCollection: (id: string, updates: Partial<ChartSetList>) => void;
  onDeleteCollection: (id: string) => void;
  onAddChartToCollection: (collectionId: string, chartTitle: string) => void;
  onRemoveChartFromCollection: (collectionId: string, chartTitle: string) => void;
  onClose: () => void;
}

const CollectionManager: React.FC<CollectionManagerProps> = ({
  collections,
  savedChartsData,
  onCreateCollection,
  onUpdateCollection,
  onDeleteCollection,
  onAddChartToCollection,
  onRemoveChartFromCollection,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'collections' | 'create'>('collections');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);

  const selectedCollection = collections.find(c => c.id === selectedCollectionId);

  const handleCreateCollection = (collection: Omit<ChartSetList, 'id' | 'createdAt' | 'lastModified'>) => {
    onCreateCollection(collection);
    setActiveTab('collections');
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 sm:p-6"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-[95vw] sm:max-w-4xl h-[95vh] sm:h-[90vh] flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex justify-between items-center p-3 sm:p-6 border-b border-gray-700">
          {selectedCollectionId ? (
            <button
              onClick={() => setSelectedCollectionId(null)}
              className="flex items-center gap-1 text-purple-400 hover:text-purple-300 sm:hidden"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <span className="text-sm">Back</span>
            </button>
          ) : null}
          <h2 className="text-lg sm:text-2xl font-bold text-white">Setlist Manager</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Tabs */}
        <div className="border-b border-gray-700">
          <div className="flex">
            <button
              onClick={() => setActiveTab('collections')}
              className={`px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold transition-colors ${
                activeTab === 'collections' 
                  ? 'text-purple-400 border-b-2 border-purple-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Setlists ({collections.length}){selectedCollection ? `: ${selectedCollection.name}` : ''}
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold transition-colors ${
                activeTab === 'create' 
                  ? 'text-purple-400 border-b-2 border-purple-400' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Create New
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="sm:flex-1 overflow-hidden min-h-0">
          {activeTab === 'collections' ? (
            <div className="flex flex-col sm:flex-row sm:h-full">
              {/* Collections List - Hidden on mobile when a collection is selected */}
              {(!selectedCollectionId || window.innerWidth >= 640) && (
                <SetlistList
                  collections={collections}
                  selectedCollectionId={selectedCollectionId}
                  onSelectCollection={setSelectedCollectionId}
                  onDeleteCollection={onDeleteCollection}
                  onCreateNew={() => setActiveTab('create')}
                />
              )}

              {/* Collection Details */}
              {selectedCollection && (
                <div className="flex-1 flex flex-col h-full min-h-0">
                  <div className="flex-1 flex flex-col sm:flex-row min-h-0 p-2 sm:p-4 gap-2 sm:gap-4">
                    <SetlistDetail
                      collection={selectedCollection}
                      onUpdateCollection={onUpdateCollection}
                      onRemoveChart={onRemoveChartFromCollection}
                    />
                    <ChartPicker
                      collection={selectedCollection}
                      savedChartsData={savedChartsData}
                      onAddChart={onAddChartToCollection}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <CreateSetlistForm
              collectionsCount={collections.length}
              onCreateCollection={handleCreateCollection}
              onCancel={() => setActiveTab('collections')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionManager;
